import {
  BadRequestException,
  Injectable,
  NotFoundException,
  Optional,
} from '@nestjs/common';
import { Prisma } from '@prisma/client';
import {
  CheckoutAddress,
  CheckoutLineItemSummary,
  CheckoutSessionSummary,
  CompleteCheckoutSessionRequest,
  CreateCheckoutSessionRequest,
  OrderSummary,
  UpdateCheckoutSessionRequest,
} from '@cart/shared-types';
import { PrismaService } from '@cart/api-data-access';
import { AuditService } from '../audit/audit.service';
import { AuditTransactionService } from '../audit/audit-transaction.service';
import { CartService } from '../cart/cart.service';
import { InventoryLedgerService } from '../inventory/inventory-ledger.service';
import { StoreContextService } from '../tenancy/store-context.service';
import { ShippingService } from '../shipping/shipping.service';

interface ReservableLineItem {
  productId: string;
  variantId?: string | null;
  quantity: number;
}

interface LineItemTaxResult {
  hsnCode?: string;
  igstRateBps: number;
  cgstRateBps: number;
  sgstRateBps: number;
  isInterState: boolean;
  taxableValue: number;
  cgstAmount: number;
  sgstAmount: number;
  igstAmount: number;
}

@Injectable()
export class CheckoutService {
  constructor(
    private readonly prisma: PrismaService,
    private readonly cartService: CartService,
    @Optional()
    private readonly auditService?: AuditService,
    @Optional()
    private readonly storeContext?: StoreContextService,
    @Optional()
    private readonly ledger?: InventoryLedgerService,
    // Optional for the same reason the others are: the specs construct this
    // service directly, and shipping resolves to zero without it rather than
    // failing a checkout.
    @Optional()
    private readonly shipping?: ShippingService,
    /**
     * Optional like the collaborators above — the specs build this service
     * bare. Without it the change rows are still written by the trigger, they
     * just carry `system` as the actor instead of the signed-in user.
     */
    @Optional()
    private readonly auditTx?: AuditTransactionService
  ) {}

  /**
   * Bring `products.Inventory.quantity` back in line with the ledger.
   *
   * Reserving raises `committed` and committing lowers `available`; both reduce
   * what a shopper can buy, and neither is visible until the cache catches up.
   * Run outside the transaction that moved the stock — this is a read model,
   * and holding the write lock while rebuilding it buys nothing.
   */
  private async refreshStockCache(productIds: string[]): Promise<void> {
    if (!this.ledger) return;
    for (const productId of new Set(productIds)) {
      await this.ledger.syncProductCache(productId);
    }
  }

  async createSession(
    userId: string,
    input: CreateCheckoutSessionRequest
  ): Promise<CheckoutSessionSummary> {
    const cart = await this.cartService.getCart(userId);

    if (!cart.items.length) {
      throw new BadRequestException('cart is empty');
    }

    // Inherit whatever coupon the shopper already applied in the cart — they
    // shouldn't have to re-type a code they already entered and had validated.
    // An explicit discountCode on the request (if a client ever sends one)
    // still wins, so nothing here can override an intentional override.
    const totals = await this.calculateTotals(
      cart.items.map((item) => ({ productId: item.productId, lineTotal: item.lineTotal })),
      cart.subtotal,
      {
        ...input,
        discountCode: input.discountCode ?? cart.couponCode,
      },
      userId
    );
    const token = this.checkoutToken();
    const expiresOn = new Date(Date.now() + 1000 * 60 * 60 * 24);
    const session = await this.prisma.$transaction(async (tx) => {
      // First statement in the transaction, deliberately: audit.fn_log_change()
      // reads this setting at the moment it fires, so anything written before
      // this point is attributed to `system`.
      await this.auditTx?.publishActor(tx);
      const created = await tx.checkoutSession.create({
        data: {
          storeId: this.storeId(),
          customerId: userId,
          cartId: cart.id,
          token,
          email: this.optionalEmail(input.email),
          shippingAddress: this.optionalAddress(input.shippingAddress),
          billingAddress: this.optionalAddress(input.billingAddress),
          subtotal: cart.subtotal,
          taxTotal: totals.taxTotal,
          cgstTotal: totals.cgstTotal,
          sgstTotal: totals.sgstTotal,
          igstTotal: totals.igstTotal,
          tcsTotal: totals.tcsTotal,
          shippingTotal: totals.shippingTotal,
          discountTotal: totals.discountTotal,
          discountCode: totals.discountCode,
          grandTotal: totals.grandTotal,
          currency: totals.currency,
          expiresOn,
        },
      });

      await Promise.all(
        cart.items.map((item, index) => {
          const tax = totals.items[index];
          return tx.checkoutLineItem.create({
            data: {
              storeId: this.storeId(),
              checkoutId: created.id,
              productId: item.productId,
              // Carried from the cart line so the order records which size was
              // bought. Without this the column stayed null and an order for
              // "1 L" was indistinguishable from one for "500 ml".
              variantId: item.variantId ?? null,
              title: item.productName,
              sku: item.productSlug,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              lineTotal: item.lineTotal,
              hsnCode: tax.hsnCode,
              igstRateBps: tax.igstRateBps,
              cgstRateBps: tax.cgstRateBps,
              sgstRateBps: tax.sgstRateBps,
              isInterState: tax.isInterState,
              taxableValue: tax.taxableValue,
              cgstAmount: tax.cgstAmount,
              sgstAmount: tax.sgstAmount,
              igstAmount: tax.igstAmount,
            },
          });
        })
      );

      await this.reserveCheckoutInventory(tx, cart.items, created.id);

      return created;
    });

    await this.refreshStockCache(cart.items.map((item) => item.productId));

    await this.auditService?.logChange({
      schemaName: 'cart',
      tableName: 'checkout_sessions',
      recordId: session.id,
      operation: 'create',
      changedColumns: [
        'items',
        'subtotal',
        'taxTotal',
        'shippingTotal',
        'discountTotal',
        'grandTotal',
      ],
      newValue: { ...session, items: cart.items },
      source: 'CheckoutService.createSession',
    });

    return this.findSession(session.token);
  }

  async findSession(token: string): Promise<CheckoutSessionSummary> {
    const session = await this.prisma.checkoutSession.findFirst({
      where: { token, storeId: this.storeId(), isActive: true },
    });

    if (!session) {
      throw new NotFoundException('checkout session not found');
    }

    const lineItems = await this.prisma.checkoutLineItem.findMany({
      where: {
        checkoutId: session.id,
        storeId: this.storeId(),
        isActive: true,
      },
      orderBy: { createdAt: 'asc' },
    });

    return this.toSummary(session, lineItems);
  }

  async updateSession(
    token: string,
    userId: string,
    input: UpdateCheckoutSessionRequest
  ): Promise<CheckoutSessionSummary> {
    const existing = await this.prisma.checkoutSession.findFirst({
      where: { token, storeId: this.storeId(), isActive: true },
      select: { id: true, subtotal: true, status: true },
    });

    if (!existing) {
      throw new NotFoundException('checkout session not found');
    }

    if (existing.status !== 'open') {
      throw new BadRequestException('checkout session is not open');
    }

    // Re-fetched (not passed in) because the address on this update — the
    // very thing being changed — can flip the intra/inter-state split for
    // every line, so the tax on each existing line has to be recomputed,
    // not just the aggregate.
    const existingLineItems = await this.prisma.checkoutLineItem.findMany({
      where: { checkoutId: existing.id, storeId: this.storeId(), isActive: true },
      select: { id: true, productId: true, lineTotal: true },
    });

    const totals = await this.calculateTotals(
      existingLineItems.map((item) => ({ productId: item.productId, lineTotal: item.lineTotal })),
      existing.subtotal,
      input,
      userId
    );
    const data: Prisma.CheckoutSessionUpdateInput = {
      taxTotal: totals.taxTotal,
      cgstTotal: totals.cgstTotal,
      sgstTotal: totals.sgstTotal,
      igstTotal: totals.igstTotal,
      tcsTotal: totals.tcsTotal,
      shippingTotal: totals.shippingTotal,
      discountTotal: totals.discountTotal,
      discountCode: totals.discountCode,
      grandTotal: totals.grandTotal,
      currency: totals.currency,
    };

    if (input.email !== undefined) data.email = this.optionalEmail(input.email);
    if (input.shippingAddress !== undefined) {
      data.shippingAddress = this.optionalAddress(input.shippingAddress);
    }
    if (input.billingAddress !== undefined) {
      data.billingAddress = this.optionalAddress(input.billingAddress);
    }
    if (input.status !== undefined) data.status = input.status;

    const [updated] = await this.prisma.$transaction([
      this.prisma.checkoutSession.update({ where: { id: existing.id }, data }),
      ...existingLineItems.map((item, index) =>
        this.prisma.checkoutLineItem.update({
          where: { id: item.id },
          data: totals.items[index],
        })
      ),
    ]);

    await this.auditService?.logChange({
      schemaName: 'cart',
      tableName: 'checkout_sessions',
      recordId: updated.id,
      operation: 'update',
      changedColumns: [
        'email',
        'shippingAddress',
        'billingAddress',
        'taxTotal',
        'shippingTotal',
        'discountTotal',
        'grandTotal',
        'status',
      ],
      newValue: updated,
      source: 'CheckoutService.updateSession',
    });

    return this.findSession(token);
  }

  async completeSession(
    token: string,
    userId: string,
    input: CompleteCheckoutSessionRequest
  ): Promise<OrderSummary> {
    const session = await this.prisma.checkoutSession.findFirst({
      where: { token, storeId: this.storeId(), isActive: true },
    });

    if (!session) {
      throw new NotFoundException('checkout session not found');
    }

    if (session.status !== 'open') {
      throw new BadRequestException('checkout session is not open');
    }

    if (session.expiresOn && session.expiresOn.getTime() <= Date.now()) {
      throw new BadRequestException('checkout session is expired');
    }

    const lineItems = await this.prisma.checkoutLineItem.findMany({
      where: {
        checkoutId: session.id,
        storeId: this.storeId(),
        isActive: true,
      },
      orderBy: { createdAt: 'asc' },
    });

    if (!lineItems.length) {
      throw new BadRequestException('checkout session has no line items');
    }

    const orderId = `order-${Date.now()}`;
    const paymentStatus = input.paymentStatus ?? 'paid';
    const paymentProvider = input.paymentProvider?.trim() || 'manual';
    const order = await this.prisma.$transaction(async (tx) => {
      // First statement in the transaction, deliberately: audit.fn_log_change()
      // reads this setting at the moment it fires, so anything written before
      // this point is attributed to `system`.
      await this.auditTx?.publishActor(tx);
      const createdOrder = await tx.order.create({
        data: {
          id: orderId,
          storeId: this.storeId(),
          customerId: userId,
          orderNumber: orderId,
          status: 'placed',
          paymentStatus,
          fulfillmentStatus: 'unfulfilled',
          email: session.email,
          currency: session.currency,
          subtotal: session.subtotal,
          taxTotal: session.taxTotal,
          cgstTotal: session.cgstTotal,
          sgstTotal: session.sgstTotal,
          igstTotal: session.igstTotal,
          tcsTotal: session.tcsTotal,
          shippingTotal: session.shippingTotal,
          discountTotal: session.discountTotal,
          grandTotal: session.grandTotal,
          shippingAddress: session.shippingAddress as Prisma.InputJsonValue,
          billingAddress: session.billingAddress as Prisma.InputJsonValue,
          placedOn: new Date(),
        },
      });

      await Promise.all(
        lineItems.map((item) =>
          tx.orderItem.create({
            data: {
              storeId: this.storeId(),
              orderId,
              productId: item.productId,
              variantId: item.variantId,
              title: item.title,
              sku: item.sku,
              quantity: item.quantity,
              unitPrice: item.unitPrice,
              lineTotal: item.lineTotal,
              taxTotal: item.cgstAmount + item.sgstAmount + item.igstAmount,
              hsnCode: item.hsnCode,
              igstRateBps: item.igstRateBps,
              cgstRateBps: item.cgstRateBps,
              sgstRateBps: item.sgstRateBps,
              isInterState: item.isInterState,
              taxableValue: item.taxableValue,
              cgstAmount: item.cgstAmount,
              sgstAmount: item.sgstAmount,
              igstAmount: item.igstAmount,
            },
          })
        )
      );

      await this.commitCheckoutInventory(tx, lineItems, createdOrder.id);

      await tx.orderStatusHistory.create({
        data: {
          storeId: this.storeId(),
          orderId,
          toStatus: 'placed',
          note: `Created from checkout ${session.token}`,
        },
      });

      const payment = await tx.payment.create({
        data: {
          storeId: this.storeId(),
          orderId,
          provider: paymentProvider,
          status: paymentStatus,
          amount: session.grandTotal,
          currency: session.currency,
          providerReference: input.paymentReference,
        },
      });

      await tx.paymentTransaction.create({
        data: {
          storeId: this.storeId(),
          paymentId: payment.id,
          type: paymentStatus === 'paid' ? 'capture' : 'authorize',
          status: paymentStatus,
          amount: session.grandTotal,
          rawPayload: {
            checkoutToken: session.token,
            providerReference: input.paymentReference,
          },
        },
      });

      await tx.checkoutSession.update({
        where: { id: session.id },
        data: { status: 'completed' },
      });

      if (session.discountCode) {
        const codes = session.discountCode
          .split(',')
          .map((c) => c.trim())
          .filter(Boolean);

        if (codes.length) {
          await tx.discountCode.updateMany({
            where: { storeId: this.storeId(), code: { in: codes } },
            data: { usedCount: { increment: 1 } },
          });
        }
      }

      return createdOrder;
    });

    // The sale has left the shelf — `available` dropped and `committed` was
    // released, so the figure the storefront prints has to be rebuilt.
    await this.refreshStockCache(lineItems.map((item) => item.productId));

    await this.cartService.clear(userId);

    const summary = this.orderSummary(order, lineItems);

    await this.auditService?.logChange({
      schemaName: 'sales',
      tableName: 'orders',
      recordId: summary.id,
      operation: 'create',
      changedColumns: [
        'checkoutSession',
        'items',
        'paymentStatus',
        'grandTotal',
      ],
      newValue: summary,
      source: 'CheckoutService.completeSession',
    });

    return summary;
  }

  private async calculateTotals(
    lineItems: Array<{ productId: string; lineTotal: number }>,
    subtotal: number,
    input: CreateCheckoutSessionRequest,
    userId?: string
  ): Promise<{
    taxTotal: number;
    cgstTotal: number;
    sgstTotal: number;
    igstTotal: number;
    tcsTotal: number;
    shippingTotal: number;
    discountTotal: number;
    discountCode: string | null;
    grandTotal: number;
    currency: string;
    items: LineItemTaxResult[];
  }> {
    // Discount resolved first (not shipping) — a free_shipping-type code needs
    // to tell resolveShipping to zero the shipping line, so it must run before it.
    const discount = await this.resolveDiscount(
      subtotal,
      input.discountCode,
      userId
    );
    const shipping = await this.resolveShipping(
      input,
      lineItems,
      subtotal,
      discount.freeShipping
    );

    const store = await this.prisma.store.findUnique({
      where: { id: this.storeId() },
      select: { state: true, tcsRateBps: true },
    });

    const tax = await this.computeLineItemTax(
      lineItems,
      discount.discountTotal,
      subtotal,
      store?.state,
      input.shippingAddress?.state
    );
    /*
     * GST on delivery.
     *
     * Delivery bundled with goods is a **composite supply**: it takes the rate
     * of the principal supply, not a rate of its own. So the cart's own
     * weighted-average rate is applied to the shipping line, split the same way
     * the goods were.
     *
     * This was missing entirely — shipping went into the total untaxed, which
     * under-collected GST on every order that charged for delivery.
     */
    const shippingTax = this.computeShippingTax(
      shipping.amount,
      tax.items,
      Boolean(store?.state && input.shippingAddress?.state &&
        store.state.trim().toLowerCase() !== input.shippingAddress.state.trim().toLowerCase())
    );

    const taxTotal =
      tax.cgstTotal + tax.sgstTotal + tax.igstTotal +
      shippingTax.cgst + shippingTax.sgst + shippingTax.igst;

    // addOnTax excludes tax-inclusive lines — their GST is already inside
    // subtotal, adding it again would double-charge the customer. See
    // computeLineItemTax()'s inline note for the full reasoning.
    const grandTotal = Math.max(
      subtotal - discount.discountTotal + shipping.amount + tax.addOnTax + shippingTax.total,
      0
    );

    // TCS (Section 52) — withheld from a seller's own payout, never added to
    // what the customer pays. Computed here purely so it can be stored for
    // the admin-only accounting view; it must never reach grandTotal or any
    // customer-facing total.
    const tcsBase = Math.max(subtotal - discount.discountTotal, 0);
    const tcsTotal = Math.round((tcsBase * (store?.tcsRateBps ?? 0)) / 10000);

    return {
      taxTotal,
      cgstTotal: tax.cgstTotal + shippingTax.cgst,
      sgstTotal: tax.sgstTotal + shippingTax.sgst,
      igstTotal: tax.igstTotal + shippingTax.igst,
      tcsTotal,
      shippingTotal: shipping.amount,
      discountTotal: discount.discountTotal,
      discountCode: discount.appliedCode,
      grandTotal,
      currency: shipping.currency,
      items: tax.items,
    };
  }

  /**
   * What delivery costs, resolved from the delivery address.
   *
   * The client no longer names a price. It previously sent a `shippingRateId`
   * that was looked up and charged as-is, with nothing checking the rate
   * against the destination — so a shopper could post the cheapest rate id
   * alongside a Guwahati address and pay local pricing. The address is now the
   * only input that can move the number.
   *
   * `pickup` is the one thing the shopper genuinely chooses, because it is not
   * a price: it is the absence of delivery.
   */
  private async resolveShipping(
    input: { shippingAddress?: { postalCode?: string; city?: string; state?: string } | null; pickup?: boolean; paymentMethod?: string },
    lineItems: Array<{ productId: string; lineTotal: number; quantity?: number }>,
    subtotal: number,
    freeShipping: boolean
  ): Promise<{ amount: number; currency: string }> {
    if (input.pickup) {
      return { amount: 0, currency: 'INR' };
    }

    const address = input.shippingAddress;
    if (!address) {
      // Early in the flow there is no address yet, so there is nothing to
      // price. updateSession() recomputes once one is chosen — the same
      // approach computeLineItemTax() already takes for inter-state GST.
      return { amount: 0, currency: 'INR' };
    }

    if (!this.shipping) return { amount: 0, currency: 'INR' };

    const products = await this.prisma.product.findMany({
      where: { id: { in: [...new Set(lineItems.map((l) => l.productId))] } },
      select: { id: true, weightGrams: true },
    });
    const weightById = new Map(products.map((p) => [p.id, p.weightGrams]));

    const quote = await this.shipping.quote(this.storeId(), {
      destination: {
        postalCode: address.postalCode,
        city: address.city,
        state: address.state,
      },
      lines: lineItems.map((line) => ({
        quantity: line.quantity ?? 1,
        linePaise: line.lineTotal,
        weightGrams: weightById.get(line.productId) ?? null,
      })),
      subtotalPaise: subtotal,
      cod: input.paymentMethod === 'cod',
      freeShipping,
    });

    if (!quote.deliverable) {
      throw new BadRequestException(
        quote.reasons[0] ?? 'This store does not deliver to that address'
      );
    }

    return { amount: quote.amountPaise + quote.codFeePaise, currency: 'INR' };
  }

  // Per-line-item GST — CGST+SGST (intra-state) or IGST (inter-state),
  // resolved per product (predefined slab profile or a custom override),
  // against a discount pro-rated by each line's share of the subtotal so a
  // cart-level coupon doesn't concentrate its whole discount on one line's
  // tax base.
  private async computeLineItemTax(
    lineItems: Array<{ productId: string; lineTotal: number }>,
    discountTotal: number,
    subtotal: number,
    storeState: string | null | undefined,
    shippingState: string | undefined
  ): Promise<{
    items: LineItemTaxResult[];
    cgstTotal: number;
    sgstTotal: number;
    igstTotal: number;
    addOnTax: number;
  }> {
    if (!lineItems.length) {
      return { items: [], cgstTotal: 0, sgstTotal: 0, igstTotal: 0, addOnTax: 0 };
    }

    // No shipping address yet (early in the flow) — can't know the real
    // destination, so preview as intra-state rather than guessing
    // inter-state; updateSession() recomputes for real once the address is
    // confirmed.
    const isInterState = Boolean(
      storeState && shippingState && storeState.trim().toLowerCase() !== shippingState.trim().toLowerCase()
    );

    const productIds = [...new Set(lineItems.map((item) => item.productId))];
    const products = await this.prisma.product.findMany({
      where: { id: { in: productIds } },
      select: {
        id: true,
        taxMode: true,
        hsnCode: true,
        isTaxInclusive: true,
        customIgstRateBps: true,
        customCgstRateBps: true,
        customSgstRateBps: true,
        taxProfile: { select: { igstRateBps: true, cgstRateBps: true, sgstRateBps: true } },
      },
    });
    const productById = new Map(products.map((product) => [product.id, product]));

    let cgstTotal = 0;
    let sgstTotal = 0;
    let igstTotal = 0;
    let addOnTax = 0;
    const items: LineItemTaxResult[] = [];

    for (const line of lineItems) {
      const product = productById.get(line.productId);
      const rates = this.resolveProductTaxRates(product);

      const itemDiscountShare = subtotal > 0 ? Math.round((discountTotal * line.lineTotal) / subtotal) : 0;
      const taxableBase = Math.max(line.lineTotal - itemDiscountShare, 0);

      let taxableValue: number;
      let totalTax: number;

      if (product?.isTaxInclusive) {
        // Extraction: the rate is already baked into taxableBase, so pull
        // the tax portion back out rather than adding more on top.
        taxableValue = Math.round((taxableBase * 10000) / (10000 + rates.igstRateBps));
        totalTax = taxableBase - taxableValue;
      } else {
        taxableValue = taxableBase;
        totalTax = Math.round((taxableBase * rates.igstRateBps) / 10000);
        addOnTax += totalTax;
      }

      // sgstAmount is the remainder (not its own round()) so cgst+sgst
      // always sums exactly to totalTax even when totalTax is odd.
      const cgstAmount = isInterState ? 0 : Math.round(totalTax / 2);
      const sgstAmount = isInterState ? 0 : totalTax - cgstAmount;
      const igstAmount = isInterState ? totalTax : 0;

      cgstTotal += cgstAmount;
      sgstTotal += sgstAmount;
      igstTotal += igstAmount;

      items.push({
        hsnCode: product?.hsnCode ?? undefined,
        igstRateBps: rates.igstRateBps,
        cgstRateBps: rates.cgstRateBps,
        sgstRateBps: rates.sgstRateBps,
        isInterState,
        taxableValue,
        cgstAmount,
        sgstAmount,
        igstAmount,
      });
    }

    return { items, cgstTotal, sgstTotal, igstTotal, addOnTax };
  }

  /**
   * GST on the delivery charge.
   *
   * The rate is the cart's own, weighted by taxable value — that is what
   * "composite supply" means: delivery is not separately rated, it inherits the
   * rate of what is being delivered. A cart of 5% groceries and 18% cookware
   * lands between the two, in proportion.
   *
   * Zero shipping, or a cart with no taxable value, produces no tax rather than
   * a division by zero.
   */
  private computeShippingTax(
    shippingAmount: number,
    items: LineItemTaxResult[],
    isInterState: boolean
  ): { cgst: number; sgst: number; igst: number; total: number } {
    const none = { cgst: 0, sgst: 0, igst: 0, total: 0 };
    if (shippingAmount <= 0 || !items.length) return none;

    const taxableValue = items.reduce((sum, item) => sum + item.taxableValue, 0);
    if (taxableValue <= 0) return none;

    const weightedBps = items.reduce(
      (sum, item) => sum + item.igstRateBps * item.taxableValue,
      0
    ) / taxableValue;

    const total = Math.round((shippingAmount * weightedBps) / 10000);
    if (total <= 0) return none;

    // sgst is the remainder, not its own round(), so the halves always sum
    // back to total even when total is odd — same rule the line items use.
    const cgst = isInterState ? 0 : Math.round(total / 2);
    return {
      cgst,
      sgst: isInterState ? 0 : total - cgst,
      igst: isInterState ? total : 0,
      total,
    };
  }

  private resolveProductTaxRates(
    product:
      | {
          taxMode: string;
          customIgstRateBps: number | null;
          customCgstRateBps: number | null;
          customSgstRateBps: number | null;
          taxProfile: { igstRateBps: number; cgstRateBps: number; sgstRateBps: number } | null;
        }
      | undefined
  ): { igstRateBps: number; cgstRateBps: number; sgstRateBps: number } {
    if (!product) {
      return { igstRateBps: 0, cgstRateBps: 0, sgstRateBps: 0 };
    }

    if (product.taxMode === 'custom') {
      return {
        igstRateBps: product.customIgstRateBps ?? 0,
        cgstRateBps: product.customCgstRateBps ?? 0,
        sgstRateBps: product.customSgstRateBps ?? 0,
      };
    }

    // 'predefined' with no profile actually assigned yet fails soft to 0% —
    // matches this app's fail-soft convention elsewhere rather than
    // blocking checkout over an incomplete product setup.
    return product.taxProfile
      ? {
          igstRateBps: product.taxProfile.igstRateBps,
          cgstRateBps: product.taxProfile.cgstRateBps,
          sgstRateBps: product.taxProfile.sgstRateBps,
        }
      : { igstRateBps: 0, cgstRateBps: 0, sgstRateBps: 0 };
  }

  private async resolveDiscount(
    subtotal: number,
    code: string | undefined,
    userId: string | undefined
  ): Promise<{
    discountTotal: number;
    freeShipping: boolean;
    appliedCode: string | null;
  }> {
    if (!code) {
      return { discountTotal: 0, freeShipping: false, appliedCode: null };
    }

    // Comma-joined = multiple stacked codes (see cart.repository's CouponRecord
    // and admin's "Stackable" toggle) — a single code is just the length-1 case.
    const codes = Array.from(
      new Set(
        code
          .split(',')
          .map((c) => c.trim().toUpperCase())
          .filter(Boolean)
      )
    );

    if (codes.length === 0) {
      return { discountTotal: 0, freeShipping: false, appliedCode: null };
    }

    const now = new Date();
    const discounts = await this.prisma.discountCode.findMany({
      where: {
        storeId: this.storeId(),
        code: { in: codes },
        isActive: true,
        OR: [{ startsOn: null }, { startsOn: { lte: now } }],
        AND: [{ OR: [{ endsOn: null }, { endsOn: { gte: now } }] }],
      },
    });

    if (discounts.length !== codes.length) {
      throw new NotFoundException('discount code not found');
    }

    if (codes.length > 1 && discounts.some((d) => !d.stackable)) {
      throw new BadRequestException(
        'one or more of these discount codes cannot be combined'
      );
    }

    let isNewCustomer: boolean | null = null;

    for (const discount of discounts) {
      if (
        discount.usageLimit != null &&
        discount.usedCount >= discount.usageLimit
      ) {
        throw new BadRequestException(
          `discount code ${discount.code} has reached its usage limit`
        );
      }

      if (
        discount.minOrderAmount != null &&
        subtotal < discount.minOrderAmount
      ) {
        throw new BadRequestException(
          `order subtotal must be at least ${discount.minOrderAmount} to use ${discount.code}`
        );
      }

      if (discount.customerEligibility === 'new_customers') {
        if (isNewCustomer === null) {
          isNewCustomer = userId
            ? (await this.prisma.order.count({
                where: { storeId: this.storeId(), customerId: userId },
              })) === 0
            : false;
        }

        if (!isNewCustomer) {
          throw new BadRequestException(
            `discount code ${discount.code} is only valid for new customers`
          );
        }
      }
    }

    let discountTotal = 0;
    for (const discount of discounts) {
      let amount =
        discount.type === 'percentage'
          ? Math.round((subtotal * discount.value) / 10000)
          : discount.type === 'fixed'
          ? discount.value
          : 0; // free_shipping — no line-item discount, only the shipping line changes

      if (discount.maxDiscountAmount != null) {
        amount = Math.min(amount, discount.maxDiscountAmount);
      }

      discountTotal += amount;
    }

    return {
      discountTotal: Math.min(discountTotal, subtotal),
      freeShipping: discounts.some((d) => d.type === 'free_shipping'),
      appliedCode: discounts.map((d) => d.code).join(','),
    };
  }

  private optionalEmail(email: string | undefined): string | undefined {
    if (email === undefined || email.trim() === '') {
      return undefined;
    }

    const normalized = email.trim().toLowerCase();

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized)) {
      throw new BadRequestException('email must be valid');
    }

    return normalized;
  }

  private optionalAddress(
    address: CheckoutAddress | undefined
  ): Prisma.InputJsonValue | undefined {
    if (address === undefined) {
      return undefined;
    }

    const required: Array<keyof CheckoutAddress> = [
      'recipientName',
      'line1',
      'city',
      'state',
      'postalCode',
      'country',
    ];

    for (const key of required) {
      if (typeof address[key] !== 'string' || address[key]?.trim() === '') {
        throw new BadRequestException(`${key} is required`);
      }
    }

    return {
      ...address,
      country: address.country.toUpperCase(),
    } as Prisma.InputJsonValue;
  }

  private toSummary(
    session: {
      id: string;
      token: string;
      status: string;
      email: string | null;
      shippingAddress: Prisma.JsonValue | null;
      billingAddress: Prisma.JsonValue | null;
      subtotal: number;
      taxTotal: number;
      cgstTotal: number;
      sgstTotal: number;
      igstTotal: number;
      shippingTotal: number;
      discountTotal: number;
      grandTotal: number;
      currency: string;
      expiresOn: Date | null;
      createdAt: Date;
    },
    lineItems: Array<{
      id: string;
      productId: string;
      variantId: string | null;
      title: string;
      sku: string | null;
      quantity: number;
      unitPrice: number;
      lineTotal: number;
      hsnCode: string | null;
      igstRateBps: number;
      cgstRateBps: number;
      sgstRateBps: number;
      isInterState: boolean;
      taxableValue: number;
      cgstAmount: number;
      sgstAmount: number;
      igstAmount: number;
    }>
  ): CheckoutSessionSummary {
    return {
      id: session.id,
      token: session.token,
      status: session.status,
      email: session.email ?? undefined,
      shippingAddress: this.checkoutAddress(session.shippingAddress),
      billingAddress: this.checkoutAddress(session.billingAddress),
      items: lineItems.map(
        (item): CheckoutLineItemSummary => ({
          id: item.id,
          productId: item.productId,
          variantId: item.variantId ?? undefined,
          title: item.title,
          sku: item.sku ?? undefined,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          lineTotal: item.lineTotal,
          hsnCode: item.hsnCode ?? undefined,
          igstRateBps: item.igstRateBps,
          cgstRateBps: item.cgstRateBps,
          sgstRateBps: item.sgstRateBps,
          isInterState: item.isInterState,
          taxableValue: item.taxableValue,
          cgstAmount: item.cgstAmount,
          sgstAmount: item.sgstAmount,
          igstAmount: item.igstAmount,
        })
      ),
      subtotal: session.subtotal,
      taxTotal: session.taxTotal,
      // Customer-facing — CGST/SGST/IGST are what the customer's own GST
      // liability breaks down into, so they're shown at checkout. tcsTotal
      // is deliberately NOT included here — see the GstTotals doc comment
      // in shared-types.
      cgstTotal: session.cgstTotal,
      sgstTotal: session.sgstTotal,
      igstTotal: session.igstTotal,
      shippingTotal: session.shippingTotal,
      discountTotal: session.discountTotal,
      grandTotal: session.grandTotal,
      currency: session.currency,
      expiresOn: session.expiresOn?.toISOString(),
      createdAt: session.createdAt.toISOString(),
    };
  }

  private checkoutToken(): string {
    return `checkout-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  private checkoutAddress(
    value: Prisma.JsonValue | null
  ): CheckoutAddress | undefined {
    return value === null ? undefined : (value as unknown as CheckoutAddress);
  }

  // Customer-facing — the order confirmation returned straight from
  // completeSession(). Same tcsTotal exclusion as toSummary() above.
  private orderSummary(
    order: {
      id: string;
      status: string;
      paymentStatus: string;
      fulfillmentStatus: string;
      subtotal: number;
      taxTotal: number;
      cgstTotal: number;
      sgstTotal: number;
      igstTotal: number;
      shippingTotal: number;
      discountTotal: number;
      grandTotal: number;
      createdAt: Date;
    },
    lineItems: Array<{
      id: string;
      productId: string;
      title: string;
      sku: string | null;
      quantity: number;
      unitPrice: number;
      lineTotal: number;
      hsnCode: string | null;
      igstRateBps: number;
      cgstRateBps: number;
      sgstRateBps: number;
      isInterState: boolean;
      taxableValue: number;
      cgstAmount: number;
      sgstAmount: number;
      igstAmount: number;
    }>
  ): OrderSummary {
    return {
      id: order.id,
      status: order.status as OrderSummary['status'],
      paymentStatus: order.paymentStatus,
      fulfillmentStatus: order.fulfillmentStatus,
      items: lineItems.map((item) => ({
        id: item.id,
        productId: item.productId,
        productName: item.title,
        productSlug: item.sku ?? item.productId,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        lineTotal: item.lineTotal,
        hsnCode: item.hsnCode ?? undefined,
        igstRateBps: item.igstRateBps,
        cgstRateBps: item.cgstRateBps,
        sgstRateBps: item.sgstRateBps,
        isInterState: item.isInterState,
        taxableValue: item.taxableValue,
        cgstAmount: item.cgstAmount,
        sgstAmount: item.sgstAmount,
        igstAmount: item.igstAmount,
      })),
      subtotal: order.subtotal,
      taxTotal: order.taxTotal,
      cgstTotal: order.cgstTotal,
      sgstTotal: order.sgstTotal,
      igstTotal: order.igstTotal,
      shippingTotal: order.shippingTotal,
      discountTotal: order.discountTotal,
      grandTotal: order.grandTotal,
      totalItems: lineItems.reduce((sum, item) => sum + item.quantity, 0),
      createdAt: order.createdAt.toISOString(),
    };
  }

  private storeId(): string {
    return this.storeContext?.storeId() ?? 'store_demo';
  }

  private async reserveCheckoutInventory(
    tx: Prisma.TransactionClient,
    lineItems: ReservableLineItem[],
    checkoutId: string
  ): Promise<void> {
    for (const item of lineItems) {
      const inventoryItem = await this.findInventoryItem(tx, item);

      if (!inventoryItem?.tracked) {
        continue;
      }

      const level = await this.findReservableLevel(tx, inventoryItem.id);

      if (!level || level.available - level.committed < item.quantity) {
        throw new BadRequestException('quantity exceeds available inventory');
      }

      await tx.inventoryLevel.update({
        where: { id: level.id },
        data: { committed: { increment: item.quantity } },
      });
      await tx.inventoryMovement.create({
        data: {
          storeId: this.storeId(),
          inventoryItemId: inventoryItem.id,
          locationId: level.locationId,
          changeType: 'reserve',
          quantityDelta: 0,
          reason: 'Checkout reservation',
          referenceType: 'checkout',
          referenceId: checkoutId,
        },
      });
    }
  }

  private async commitCheckoutInventory(
    tx: Prisma.TransactionClient,
    lineItems: ReservableLineItem[],
    orderId: string
  ): Promise<void> {
    for (const item of lineItems) {
      const inventoryItem = await this.findInventoryItem(tx, item);

      if (!inventoryItem?.tracked) {
        continue;
      }

      const level = await this.findCommittedLevel(tx, inventoryItem.id);

      if (
        !level ||
        level.committed < item.quantity ||
        level.available < item.quantity
      ) {
        throw new BadRequestException('quantity exceeds reserved inventory');
      }

      await tx.inventoryLevel.update({
        where: { id: level.id },
        data: {
          available: { decrement: item.quantity },
          committed: { decrement: item.quantity },
        },
      });
      await tx.inventoryMovement.create({
        data: {
          storeId: this.storeId(),
          inventoryItemId: inventoryItem.id,
          locationId: level.locationId,
          changeType: 'commit',
          quantityDelta: -item.quantity,
          reason: 'Order placed',
          referenceType: 'order',
          referenceId: orderId,
        },
      });
    }
  }

  private async findInventoryItem(
    tx: Prisma.TransactionClient,
    lineItem: ReservableLineItem
  ): Promise<{ id: string; tracked: boolean } | null> {
    return tx.inventoryItem.findFirst({
      where: {
        storeId: this.storeId(),
        productId: lineItem.productId,
        variantId: lineItem.variantId ?? null,
        isActive: true,
      },
      select: { id: true, tracked: true },
    });
  }

  private async findReservableLevel(
    tx: Prisma.TransactionClient,
    inventoryItemId: string
  ): Promise<{
    id: string;
    locationId: string;
    available: number;
    committed: number;
  } | null> {
    return tx.inventoryLevel.findFirst({
      where: {
        storeId: this.storeId(),
        inventoryItemId,
        isActive: true,
      },
      orderBy: [{ available: 'desc' }],
      select: { id: true, locationId: true, available: true, committed: true },
    });
  }

  private async findCommittedLevel(
    tx: Prisma.TransactionClient,
    inventoryItemId: string
  ): Promise<{
    id: string;
    locationId: string;
    available: number;
    committed: number;
  } | null> {
    return tx.inventoryLevel.findFirst({
      where: {
        storeId: this.storeId(),
        inventoryItemId,
        isActive: true,
      },
      orderBy: [{ committed: 'desc' }],
      select: { id: true, locationId: true, available: true, committed: true },
    });
  }
}
